import React from 'react';
import useCheckoutState from '../hooks/useCheckoutState';
import { getEventConfirmation } from './get-event-confirmation';
import MultilineText from '../components/MultilineText';
import Button from '../components/Button';
import getButtonBackgroundColor from '../lib/getButtonBackgroundColor';
import getButtonTextColor from '../lib/getButtonTextColor';
import { CalendarIcon, MapPinIcon, TicketIcon, VideoCameraIcon } from '@heroicons/react/24/outline';
import { formatEventDate } from '../lib/format-date';
import { useCheckoutLocalization } from '../hooks/useCheckoutLocalization';

const EventConfirmation = ({ payment, isConfirmation }) => {
  const { restartPayment, checkout, seller } = useCheckoutState();
  const { locale } = useCheckoutLocalization();

  // This payment has been canceled by the merchant.
  if (payment.orderStatus === 'canceled') {
    return (
      <div className="cp_status-wrapper text-center p-4 w-full">
        <h2 className="cp_status-title text-black mb-2">Booking canceled</h2>
        <div className="cp_status-content mb-4">Your booking has been canceled.</div>
      </div>
    );
  }

  switch (payment.status) {
    case 'pending':
    case 'partially_paid':
    case 'unpaid':
    case 'refunded':
    case 'partial_refund':
    case 'paid': {
      const { title, message, type } = getEventConfirmation({
        checkout,
        charge: payment,
        seller,
        email: payment.customer.email,
      });

      return (
        <>
          <div className="cp_status-wrapper text-center p-4 w-full">
            <h2 className="cp_status-title text-black mb-2">
              <div
                className="flex justify-center mb-4 cp_status-icon"
                style={{
                  color: seller.brandColor,
                }}
              >
                <svg width="56" height="56" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="m19 28 6 6 12-12m18 6a27 27 0 1 1-54 0 27 27 0 0 1 54 0Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              {title}
            </h2>
            <div className="cp_status-content mb-4">
              {type === 'html' ? (
                <MultilineText
                  className="cp_status-text"
                  dangerouslySetInnerHTML={{
                    __html: message,
                  }}
                />
              ) : (
                <MultilineText className="cp_status-text">{message}</MultilineText>
              )}
            </div>

            {checkout.eventDetails && (
              <div className="cp_status-event-details text-center space-y-2 mb-4">
                {checkout.showConfirmationEventName && <h2>{checkout.title}</h2>}

                {checkout.showConfirmationEventDateTime && checkout.eventDetails.startDate && (
                  <div className="cp_status-event-details-date-wrapper flex items-start gap-x-2 text-center justify-center">
                    <div className="cp_status-event-details-date-icon-wrapper mt-0.5">
                      <CalendarIcon className="cp_status-event-details-date-icon h-5 w-5" />
                    </div>
                    <div className="cp_status-event-details-date flex flex-col font-medium">
                      {formatEventDate({
                        startDate: checkout.eventDetails.startDate,
                        endDate: checkout.eventDetails.endDate,
                        timezone: checkout.eventDetails.timezone,
                        locale,
                      }).map(date => (
                        <span key={date}>{date}</span>
                      ))}
                    </div>
                  </div>
                )}

                {checkout.showConfirmationEventLocation &&
                  checkout.eventDetails.location &&
                  (checkout.eventDetails.type === 'in_person' ||
                    checkout.eventDetails.type === 'hybrid') && (
                    <div className="cp_status-event-details-location-wrapper flex items-center gap-x-2 text-center justify-center">
                      <div className="cp_status-event-details-location-icon-wrapper">
                        <MapPinIcon className="cp_status-event-details-location-icon h-5 w-5" />
                      </div>
                      {checkout.eventDetails.mapsLink && (
                        <a
                          href={checkout.eventDetails.mapsLink}
                          target="_blank"
                          rel="nofollow noopener noreferrer"
                          className="cp_status-event-details-location-link underline"
                        >
                          <span className="cp_status-event-details-location font-medium">
                            {checkout.eventDetails.location}
                          </span>
                        </a>
                      )}

                      {!checkout.eventDetails.mapsLink && (
                        <span className="cp_status-event-details-location font-medium">
                          {checkout.eventDetails.location}
                        </span>
                      )}
                    </div>
                  )}

                {checkout.showConfirmationEventMeetingLink &&
                  (checkout.eventDetails.type === 'virtual' ||
                    checkout.eventDetails.type === 'hybrid') && (
                    <div
                      className={
                        'cp_event-details-virtual-wrapper flex items-center gap-x-2 text-center justify-center'
                      }
                    >
                      <div className="cp_event-details-virtual-icon-wrapper">
                        <VideoCameraIcon className="cp_event-details-virtual-icon h-5 w-5" />
                      </div>
                      <span className="cp_event-details-virtual font-medium">Virtual event</span>
                    </div>
                  )}
              </div>
            )}
            <div className="cp_event-details-tickets-purchased">
              {checkout.showConfirmationEventTickets &&
                payment.tickets &&
                payment.tickets.length > 0 && (
                  <>
                    <h4 className="cp_event-details-tickets-purchased-heading mb-2">
                      {checkout.ticketsPurchasedText || 'Tickets purchased'}
                    </h4>
                    {payment.tickets.map(ticket => (
                      <div
                        className="cp_event-details-tickets-purchased-wrapper flex items-center gap-x-2 text-center justify-center"
                        key={ticket.ticketType}
                      >
                        <TicketIcon className="cp_event-details-tickets-purchased-tickets-icon h-5 w-5" />
                        <span className="cp_event-details-tickets-purchased-tickets font-medium">
                          {ticket.quantity}x {ticket.name}
                        </span>
                      </div>
                    ))}
                  </>
                )}
            </div>
          </div>
        </>
      );
    }

    case 'failed':
    case 'canceled':
      return (
        <div className="cp_status-wrapper text-center p-4 w-full">
          <h2 className="cp_status-title text-black mb-2">Payment failed</h2>
          <div className="cp_status-content mb-4">
            Your payment failed and has not been processed.
            <br />
            Please try again.
          </div>
          <Button
            handleClick={() => {
              restartPayment({ isConfirmation });
            }}
            appearance="black"
            className="cp_button"
            size="full"
            backgroundColor={getButtonBackgroundColor({
              buttonBackgroundColor: checkout.buttonBackgroundColor,
              brandColor: seller.brandColor,
            })}
            color={getButtonTextColor({
              buttonTextColor: checkout.buttonTextColor,
              buttonBackgroundColor: checkout.buttonBackgroundColor,
              brandColor: seller.brandColor,
            })}
          >
            Try again
          </Button>
        </div>
      );
    default:
      return (
        <div className="cp_status-wrapper text-center p-4 w-full">
          <h2 className="cp_status-title text-black mb-2">Payment pending</h2>
          <div className="cp_status-content mb-4">
            Your payment has been received and is awaiting payment confirmation. Please contact{' '}
            {seller.displayName || 'the seller'} to confirm your payment.
          </div>
        </div>
      );
  }
};

export default EventConfirmation;
